/* eslint-disable react/jsx-max-depth */
import { Form, Formik } from 'formik';

import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Button from '@mui/material/Button';

import { ensureDefined } from '@e-socna/utils';
import {
  PRINT_ADD_PRINT_FOOTERS_ID,
  PRINT_ADD_PRINT_HEADERS_ID,
  PRINT_ADD_GROUP_FOOTERS_ID,
  PRINT_ADD_GROUP_HEADERS_ID,
  PRINT_FILE_CATEGORY_ID_ID,
  PRINT_GROUP_PAPER_FORMAT_ID,
  PRINT_GROUP_INDEX_ID,
  PRINT_GROUP_ORIENTATION_ID,
  PRINT_GROUPS_COUNT_ID,
  PRINT_SECTION_TITLES_LIST_ID,
  PRINT_SECTION_DISPLAY_FIRST_PAGE_NUMBERS_ID,
  PRINT_STEP_ID,
  PRINT_STYLE_ID_ID,
  PRINT_STYLE_CODE_ID,
  PRINT_SUBMIT_ID,
  PRINT_TOTAL_DISPLAY_PAGE_COUNT_ID,
  PRINT_RESET_ID,
  PRINT_SUPPORT_PAGE_COUNT_ID,
  PRINT_CODE_ID,
  PRINT_TITLE_ID,
  PRINT_NEED_PRINT_FEEDBACKS_ID,
  PRINT_PDF_PRINTS_INCREMENT_ID,
} from '@e-socna/printer-contract';
import type { PrintStyleId } from '@e-socna/data-layer';

import { getTouchedError } from '../../utils/get-touched-error.util';
import { useUiContext } from '../../contexts/UiContext/hooks/useUiContext.hook';
import { pathToChapters } from '../../components/DocumentEditor';
import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useDocumentEditorContext } from '../../components/DocumentEditor/contexts/DocumentEditorContext/hooks/useDocumentEditorContext.hook';
import { HIDE_ON_PRINT_CLASSNAME } from '../../components/GlobalStyles/utils/get-socna-printer-styles.util';
import type { Editable } from '../../components/DocumentEditor/types/editable.type';
import type { PrintGroup } from '../../components/DocumentEditor';

import { useValidate } from './hooks/useValidate.hook';
import { useInitialValuesFactory } from './hooks/useInitialValuesFactory.hook';
import type { PrinterFormValues } from './types/printer-form-values.type';

export type { PrinterFormValues };

interface Props {
  readonly code?: string;
  readonly contentToPrint: readonly PrintGroup[];
  readonly onReset: () => void;
  readonly onSuccess?: () => void;
  readonly title?: string;
  readonly useSubmitFactory: (
    handleSuccess?: () => void,
  ) => (
    values: Readonly<PrinterFormValues>,
    {
      setSubmitting,
    }: Readonly<{ setSubmitting: (isSubmitting: boolean) => void }>,
  ) => void;
}

export const PrinterForm = ({
  code,
  contentToPrint,
  onReset: handleReset,
  onSuccess: handleSuccess,
  title,
  useSubmitFactory,
}: Readonly<Props>) => {
  const {
    state: { editable },
    props: {
      getters: { printing: getPrinting, storage: getStorage },
    },
  } = useDocumentEditorContext<Editable>();

  const {
    pdfPrintsIncrement,
    printSettings: { addFooters, printStyleId, addHeaders },
  } = getPrinting(editable);

  const { pdfCategory } = getStorage(editable);
  const { pdfPrintFileCategoryId } = ensureDefined({
    pdfPrintFileCategoryId: pdfCategory?._id,
  });

  const { sectionTypesIdMap, printStylesIdMap } = useReferencesContext();

  const { printStyle } = ensureDefined({
    printStyle: printStylesIdMap[printStyleId as PrintStyleId],
  });

  const { ui } = useUiContext();

  let needsPrintFeedbacks = addHeaders || addFooters;
  const sectionTitles = [];

  for (const group of contentToPrint) {
    for (const section of group.sections) {
      needsPrintFeedbacks =
        needsPrintFeedbacks ||
        (section.typeId
          ? !!sectionTypesIdMap[section.typeId]?.needsPrintFeedbacks
          : false);

      if (
        group.supportPageCount &&
        !!section.displayPath &&
        section.displayPath.length &&
        !!section.title
      ) {
        // eslint-disable-next-line functional/immutable-data
        sectionTitles.push(
          `${pathToChapters(section.displayPath)} ${section.title}`,
        );
      }
    }
  }

  const { initialValues } = useInitialValuesFactory({
    sectionTitles,
    printStyle,
    fileCategoryId: pdfPrintFileCategoryId,
    code,
    title,
    pdfPrintsIncrement,
    contentToPrint,
    needsPrintFeedbacks,
    addFooters,
    addHeaders,
  });

  const { validate } = useValidate({ contentToPrint });

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={useSubmitFactory(handleSuccess)}
      validate={validate}
      validateOnBlur
      validateOnChange={false}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit: formikSubmit,
        isSubmitting,
        isValid,
        touched,
        values,
      }) => {
        const isLoading = isSubmitting || ui.isBackgroundOperationRunning;

        const currentGroupIndex = parseInt(values.currentGroupIndex, 10);

        const { currentGroup } = ensureDefined({
          currentGroup:
            contentToPrint[
              Number.isNaN(currentGroupIndex) ? 0 : currentGroupIndex
            ],
        });

        const {
          orientation: orientationValue,
          paperFormat: formatValue,
          addHeaders: addGroupHeadersValue,
          addFooters: addGroupFootersValue,
          supportPageCount: supportPageCountValue,
        } = currentGroup;

        return (
          <div className={HIDE_ON_PRINT_CLASSNAME}>
            <Form onSubmit={formikSubmit}>
              <Grid container spacing={2}>
                <Grid size={12} sx={{ pt: 1, pb: 1 }}>
                  <TextField
                    error={!!(errors.step && touched.step)}
                    fullWidth
                    helperText={getTouchedError(errors, touched, 'step')}
                    id={PRINT_STEP_ID}
                    label="Étape d'impression"
                    name="step"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    required
                    type="text"
                    value={values.step}
                    variant="outlined"
                  />
                </Grid>
                <Grid size={12} sx={{ pt: 1, pb: 1 }}>
                  <TextField
                    disabled
                    error={
                      !!(errors.printGroupsCount && touched.printGroupsCount)
                    }
                    fullWidth
                    helperText={getTouchedError(
                      errors,
                      touched,
                      'printGroupsCount',
                    )}
                    id={PRINT_GROUPS_COUNT_ID}
                    label="Nombre total de groupes d'impression"
                    name="printGroupsCount"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    required
                    type="text"
                    value={values.printGroupsCount}
                    variant="outlined"
                  />
                </Grid>
                <Grid size={12} sx={{ pt: 1, pb: 1 }}>
                  <TextField
                    error={
                      !!(errors.currentGroupIndex && touched.currentGroupIndex)
                    }
                    fullWidth
                    helperText={getTouchedError(
                      errors,
                      touched,
                      'currentGroupIndex',
                    )}
                    id={PRINT_GROUP_INDEX_ID}
                    label="Index du groupe d'impression"
                    name="currentGroupIndex"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    required
                    type="text"
                    value={values.currentGroupIndex}
                    variant="outlined"
                  />
                </Grid>
                <Grid size={12} sx={{ pt: 1, pb: 1 }}>
                  <TextField
                    disabled
                    fullWidth
                    id={PRINT_GROUP_ORIENTATION_ID}
                    label="Orientation du groupe à imprimer"
                    required
                    type="text"
                    value={orientationValue}
                    variant="outlined"
                  />
                </Grid>
                <Grid size={12} sx={{ pt: 1, pb: 1 }}>
                  <TextField
                    disabled
                    fullWidth
                    id={PRINT_GROUP_PAPER_FORMAT_ID}
                    label="Format du groupe à imprimer"
                    required
                    type="text"
                    value={formatValue}
                    variant="outlined"
                  />
                </Grid>
                <Grid size={12} sx={{ pt: 1, pb: 1 }}>
                  <FormControl fullWidth>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={addGroupHeadersValue}
                          disabled
                          id={PRINT_ADD_GROUP_HEADERS_ID}
                        />
                      }
                      disabled
                      label="Inclure les entêtes de page dans le groupe"
                      value={addGroupHeadersValue}
                    />
                  </FormControl>
                </Grid>
                <Grid size={12} sx={{ pt: 1, pb: 1 }}>
                  <FormControl fullWidth>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={addGroupFootersValue}
                          disabled
                          id={PRINT_ADD_GROUP_FOOTERS_ID}
                        />
                      }
                      disabled
                      label="Inclure les pieds de page dans le groupe"
                      value={addGroupFootersValue}
                    />
                  </FormControl>
                </Grid>
                <Grid size={12} sx={{ pt: 1, pb: 1 }}>
                  <FormControl fullWidth>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={supportPageCountValue}
                          disabled
                          id={PRINT_SUPPORT_PAGE_COUNT_ID}
                        />
                      }
                      disabled
                      label="Gérer la numérotation des pages"
                      value={supportPageCountValue}
                    />
                  </FormControl>
                </Grid>
                <Grid size={12} sx={{ pt: 1, pb: 1 }}>
                  <FormControl
                    error={!!(errors.addHeaders && touched.addHeaders)}
                    fullWidth
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={addHeaders}
                          disabled
                          id={PRINT_ADD_PRINT_HEADERS_ID}
                          name="addHeaders"
                          onBlur={handleBlur}
                          onChange={handleChange}
                        />
                      }
                      disabled
                      label="Inclure les entêtes de page dans l'impression"
                      value={addHeaders}
                    />
                    {!!(errors.addHeaders && touched.addHeaders) && (
                      <FormHelperText>{errors.addHeaders}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid size={12} sx={{ pt: 1, pb: 1 }}>
                  <FormControl
                    error={!!(errors.addFooters && touched.addFooters)}
                    fullWidth
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={addFooters}
                          disabled
                          id={PRINT_ADD_PRINT_FOOTERS_ID}
                          name="addFooters"
                          onBlur={handleBlur}
                          onChange={handleChange}
                        />
                      }
                      disabled
                      label="Inclure les pieds de page dans l'impression"
                      value={addFooters}
                    />
                    {!!(errors.addFooters && touched.addFooters) && (
                      <FormHelperText>{errors.addFooters}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid size={12} sx={{ pt: 1, pb: 1 }}>
                  <TextField
                    disabled
                    error={!!(errors.styleId && touched.styleId)}
                    fullWidth
                    helperText={getTouchedError(errors, touched, 'styleId')}
                    id={PRINT_STYLE_ID_ID}
                    label="Identifiant du style à employer"
                    name="styleId"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    required
                    type="text"
                    value={values.styleId}
                    variant="outlined"
                  />
                </Grid>
                <Grid size={12} sx={{ pt: 1, pb: 1 }}>
                  <TextField
                    disabled
                    error={!!(errors.styleCode && touched.styleCode)}
                    fullWidth
                    helperText={getTouchedError(errors, touched, 'styleCode')}
                    id={PRINT_STYLE_CODE_ID}
                    label="Code du style à employer"
                    name="styleCode"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    required
                    type="text"
                    value={values.styleCode}
                    variant="outlined"
                  />
                </Grid>
                <Grid size={12} sx={{ pt: 1, pb: 1 }}>
                  <TextField
                    disabled
                    error={
                      !!(
                        errors.pdfPrintsIncrement && touched.pdfPrintsIncrement
                      )
                    }
                    fullWidth
                    helperText={getTouchedError(
                      errors,
                      touched,
                      'pdfPrintsIncrement',
                    )}
                    id={PRINT_PDF_PRINTS_INCREMENT_ID}
                    label="Increment d'impression du document"
                    name="pdfPrintsIncrement"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    required
                    type="text"
                    value={values.pdfPrintsIncrement}
                    variant="outlined"
                  />
                </Grid>
                <Grid size={12} sx={{ pt: 1, pb: 1 }}>
                  <TextField
                    disabled
                    error={!!(errors.fileCategoryId && touched.fileCategoryId)}
                    fullWidth
                    helperText={getTouchedError(
                      errors,
                      touched,
                      'fileCategoryId',
                    )}
                    id={PRINT_FILE_CATEGORY_ID_ID}
                    label="Identifiant de la catégorie de fichier"
                    name="fileCategoryId"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    required
                    type="text"
                    value={values.fileCategoryId}
                    variant="outlined"
                  />
                </Grid>
                <Grid size={12} sx={{ pt: 1, pb: 1 }}>
                  <TextField
                    disabled
                    error={
                      !!(errors.sectionTitlesList && touched.sectionTitlesList)
                    }
                    fullWidth
                    helperText={getTouchedError(
                      errors,
                      touched,
                      'sectionTitlesList',
                    )}
                    id={PRINT_SECTION_TITLES_LIST_ID}
                    label="Liste des titres de section pour lesquelles il faut déterminer les numéros de page de début"
                    name="sectionTitlesList"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    required
                    type="text"
                    value={values.sectionTitlesList}
                    variant="outlined"
                  />
                </Grid>
                <Grid size={12} sx={{ pt: 1, pb: 1 }}>
                  <TextField
                    error={
                      !!(
                        errors.sectionDisplayFirstPageNumbers &&
                        touched.sectionDisplayFirstPageNumbers
                      )
                    }
                    fullWidth
                    helperText={getTouchedError(
                      errors,
                      touched,
                      'sectionDisplayFirstPageNumbers',
                    )}
                    id={PRINT_SECTION_DISPLAY_FIRST_PAGE_NUMBERS_ID}
                    label="Table des numéros de page (affiché) de début des sections"
                    name="sectionDisplayFirstPageNumbers"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    required={values.step === '1'}
                    type="text"
                    value={values.sectionDisplayFirstPageNumbers}
                    variant="outlined"
                  />
                </Grid>
                <Grid size={12} sx={{ pt: 1, pb: 1 }}>
                  <TextField
                    error={
                      !!(
                        errors.totalDisplayPageCount &&
                        touched.totalDisplayPageCount
                      )
                    }
                    fullWidth
                    helperText={getTouchedError(
                      errors,
                      touched,
                      'totalDisplayPageCount',
                    )}
                    id={PRINT_TOTAL_DISPLAY_PAGE_COUNT_ID}
                    label="Nombre total (affiché) de pages du document"
                    name="totalDisplayPageCount"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    required={values.step === '1'}
                    type="text"
                    value={values.totalDisplayPageCount}
                    variant="outlined"
                  />
                </Grid>
                <Grid size={12} sx={{ pt: 1, pb: 1 }}>
                  <TextField
                    disabled
                    error={!!(errors.code && touched.code)}
                    fullWidth
                    helperText={getTouchedError(errors, touched, 'code')}
                    id={PRINT_CODE_ID}
                    label="Code du document PDF"
                    name="code"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    required={false}
                    type="text"
                    value={values.code}
                    variant="outlined"
                  />
                </Grid>
                <Grid size={12} sx={{ pt: 1, pb: 1 }}>
                  <TextField
                    disabled
                    error={!!(errors.title && touched.title)}
                    fullWidth
                    helperText={getTouchedError(errors, touched, 'title')}
                    id={PRINT_TITLE_ID}
                    label="Titre du document"
                    name="title"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    required={false}
                    type="text"
                    value={values.title}
                    variant="outlined"
                  />
                </Grid>
                <Grid size={12} sx={{ pt: 1, pb: 1 }}>
                  <FormControl
                    error={
                      !!(
                        errors.needsPrintFeedbacks &&
                        touched.needsPrintFeedbacks
                      )
                    }
                    fullWidth
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={needsPrintFeedbacks}
                          disabled
                          id={PRINT_NEED_PRINT_FEEDBACKS_ID}
                          name="needsPrintFeedbacks"
                          onBlur={handleBlur}
                          onChange={handleChange}
                        />
                      }
                      disabled
                      label="Ce document nécessite de générer les feedbacks d'impression"
                      value={needsPrintFeedbacks}
                    />
                    {!!(
                      errors.needsPrintFeedbacks && touched.needsPrintFeedbacks
                    ) && (
                      <FormHelperText>
                        {errors.needsPrintFeedbacks}
                      </FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid size={12} sx={{ pt: 1, pb: 1 }}>
                  <Stack direction="row" spacing={2}>
                    <Button
                      disabled={!isValid}
                      id={PRINT_SUBMIT_ID}
                      loading={isLoading}
                      size="large"
                      type="submit"
                      variant="contained"
                    >
                      Valider
                    </Button>
                    <Button
                      disabled={isLoading}
                      id={PRINT_RESET_ID}
                      onClick={handleReset}
                      size="large"
                      variant="outlined"
                    >
                      Réinitialiser
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </Form>
          </div>
        );
      }}
    </Formik>
  );
};
